import React, { useState, useEffect } from "react";
import axios from "axios";
import { api, API_BASE_URL } from "../../config/api";
import { Table, Button, Dropdown, Menu, Modal } from "antd";
import { FaEllipsisV, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Loader from "../shared/loader/loader";

const IORSettingsTable = ({ iorSearchQuery, unloadCountryQuery }) => {
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [expandedRows, setExpandedRows] = useState({});
  const [showAllCountriesModal, setShowAllCountriesModal] = useState(false);
  const [allCountries, setAllCountries] = useState([]);
  // CHANGED: Added page state for pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Default page size
  // CHANGED: Updated useQuery to include pagination
  const {
    data,
    isLoading,
    isError,
    error,
  } = useQuery(
    ["estimates", page, pageSize, iorSearchQuery, unloadCountryQuery],
    async () => {
      const res = await api.get(`${API_BASE_URL}/estimates/estimatesPagination`, {
        params: { page, limit: pageSize, iorSearchQuery, unloadCountryQuery },
      });
      return res.data;
    },
    {
      refetchOnWindowFocus: false,
      refetchInterval: false,
    }
  );

  const estimates = data?.data || [];
  const pagination = data?.pagination || { total: 0, page: 1, limit: pageSize };

  const deleteMutation = useMutation(
    async (estimateId) => {
      const response = await api.delete(
        `${API_BASE_URL}/estimates/${estimateId}`
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["estimates"]);
        toast.success("Estimate deleted successfully!");
        setShowDeleteConfirmationModal(false);
        setSelectedEstimate(null);
      },
      onError: (error) => {
        console.error("Error deleting estimate:", error);
        toast.error(`Could not delete estimate: ${error.message}`);
      },
    }
  );

  const handleMenuClick = ({ key, item }) => {
    if (key === "edit") {
      handleEditEstimate(item);
    } else if (key === "delete") {
      setSelectedEstimate(item);
      setShowDeleteConfirmationModal(true);
    }
  };

  const handleEditEstimate = (estimate) => {
    navigate(`/edit-ior/${estimate.estimateId}`);
  };

  const handleDelete = () => {
    if (selectedEstimate && selectedEstimate.estimateId) {
      deleteMutation.mutate(selectedEstimate.estimateId);
    } else {
      toast.error("No estimate selected for deletion");
      setShowDeleteConfirmationModal(false);
    }
  };

  const toggleExpand = (record) => {
    const countries = Array.isArray(record.loadCountry)
      ? record.loadCountry
      : [record.loadCountry];
    setAllCountries(countries);
    setShowAllCountriesModal(true);
  };

  const columns = [
    {
      title: "Load Country",
      dataIndex: "loadCountry",
      key: "loadCountry",
      align: "center",
      render: (loadCountry, record) => {
        const countries = Array.isArray(loadCountry)
          ? loadCountry
          : [loadCountry];
        const displayCountries = countries.slice(0, 2);

        return (
          <div className="flex flex-col items-center">
            <div className="text-center">
              {displayCountries.join(", ")}
              {countries.length > 2 && "..."}
            </div>
            {countries.length > 2 && (
              <Button
                type="link"
                onClick={() => toggleExpand(record)}
                className="flex items-center justify-center text-blue-500 hover:text-blue-700 transition-colors duration-300 mt-1"
              >
                <span className="mr-1">See More</span>
              </Button>
            )}
          </div>
        );
      },
    },
    {
      title: "Unload Country",
      dataIndex: "unloadCountry",
      key: "unloadCountry",
      align: "center",
    },
    {
      title: "Currency",
      dataIndex: "airfreightCurrency",
      key: "airfreightCurrency",
      align: "center",
    },
    {
      title: "Weight Unit",
      dataIndex: "afcWeightUnit",
      key: "afcWeightUnit",
      align: "center",
    },
    {
      title: "Selected Template",
      dataIndex: ["selectedTemplate", "name"],
      key: "selectedTemplate",
      align: "center",
      render: (text, record) => record.selectedTemplate?.name || "N/A",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Dropdown
          overlay={
            <Menu
              onClick={(e) => handleMenuClick({ key: e.key, item: record })}
            >
              <Menu.Item key="edit">Edit</Menu.Item>
              <Menu.Item key="delete">Delete</Menu.Item>
            </Menu>
          }
        >
          <Button icon={<FaEllipsisV />} />
        </Dropdown>
      ),
    },
  ];
  // Pagination configuration
  const paginationConfig = {
    current: pagination.page,
    pageSize: pageSize,
    total: pagination.total,
    showSizeChanger: true, // CHANGED: Enable page size selector
    pageSizeOptions: [10, 20, 50, 100], // CHANGED: Available page size options
    onShowSizeChange: (current, size) => {
      // CHANGED: Handle page size change
      setPageSize(size);
      setPage(1); // Reset to first page when changing page size
    },
    onChange: (newPage, newPageSize) => {
      // CHANGED: Handle page change
      setPage(newPage);
      // Update page size if it's different
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
      }
    },
  };

  if (isLoading) return <div className="w-full mt-0"><Loader /> </div>;
  if (isError) return <div>Error: {error.message}</div>;

  return (
    <>
      {estimates.length === 0 ? (
        <div className="text-center mt-2">No Data Found</div>
      ) : (
        // CHANGED: Added pagination to Table
        <div className="mt-2">
          <Table
            dataSource={estimates}
            columns={columns}
            rowKey="_id"
            pagination={paginationConfig}
          />
        </div>
      )}
      <Modal
        title="Confirm Delete"
        visible={showDeleteConfirmationModal}
        onOk={handleDelete}
        onCancel={() => setShowDeleteConfirmationModal(false)}
      >
        <p>Are you sure you want to delete this estimate?</p>
      </Modal>
      <Modal
        title="All Load Countries"
        visible={showAllCountriesModal}
        onOk={() => setShowAllCountriesModal(false)}
        onCancel={() => setShowAllCountriesModal(false)}
        footer={[
          <div
            key="footer"
            style={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <Button
              key="ok"
              type="primary"
              onClick={() => setShowAllCountriesModal(false)}
            >
              OK
            </Button>
          </div>,
        ]}
      >
        <ul style={{ maxHeight: "400px", overflowY: "auto" }}>
          {allCountries.map((country, index) => (
            <li key={index}>{country}</li>
          ))}
        </ul>
      </Modal>
    </>
  );
};

export default IORSettingsTable;